import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowDownRight, ArrowUpRight, Receipt, ClipboardCheck, PlusCircle, Wallet, Building2 } from "lucide-react";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatCard({ icon: Icon, label, byCurrency, tone = "slate", testid }) {
  const toneMap = {
    green: "text-emerald-600 bg-emerald-50",
    red: "text-red-600 bg-red-50",
    amber: "text-amber-600 bg-amber-50",
    slate: "text-slate-600 bg-slate-100",
  };
  const entries = Object.entries(byCurrency || {});
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
          <Icon size={18} />
        </div>
        <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
      </div>
      <div className="mt-4 space-y-1">
        {entries.length === 0 && <div className="text-slate-400 text-sm">No activity today</div>}
        {entries.map(([cur, val]) => (
          <div key={cur} className="flex items-baseline justify-between">
            <span className="text-xs text-slate-500 font-mono">{cur}</span>
            <span className="tabular text-lg font-semibold text-slate-900">{fmt(val)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cashBalances = (data?.balances || []).filter((b) =>
    /^Cash|WeChat|Alipay|UPI/i.test(b.account)
  );
  const bankBalances = (data?.balances || []).filter((b) => !cashBalances.includes(b));

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm">Today&apos;s activity and current balances by currency</p>
        </div>
        <button
          onClick={() => nav("/entry")}
          data-testid="dashboard-new-entry-btn"
          className="hidden sm:inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold rounded-xl px-5 h-12 transition-colors"
        >
          <PlusCircle size={18} /> New Entry
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={ArrowDownRight} label="Received Today" byCurrency={data.today_in} tone="green" testid="stat-received" />
            <StatCard icon={ArrowUpRight} label="Paid Today" byCurrency={data.today_out} tone="red" testid="stat-paid" />
            <StatCard icon={Receipt} label="Expenses Today" byCurrency={data.today_expense} tone="amber" testid="stat-expenses" />
            <div className="border border-slate-200 rounded-xl bg-white p-5" data-testid="stat-pending">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 text-slate-600">
                  <ClipboardCheck size={18} />
                </div>
                <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Pending</div>
              </div>
              <div className="mt-4 space-y-1">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-slate-500">Awaiting Tally</span>
                  <span className="tabular text-lg font-semibold text-slate-900">{data.pending_tally}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-slate-500">Clarification</span>
                  <span className="tabular text-lg font-semibold text-slate-900">{data.pending_clarify}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Balances */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-8">
            <BalancePanel title="Cash & Wallet" icon={Wallet} items={cashBalances} testid="cash-balances" />
            <BalancePanel title="Bank Accounts" icon={Building2} items={bankBalances} testid="bank-balances" />
          </div>

          {/* Recent */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl font-semibold text-slate-900">Recent Entries</h2>
              <button
                onClick={() => nav("/entries")}
                data-testid="view-all-entries"
                className="text-sm text-[#0055FF] font-semibold hover:underline"
              >
                View all →
              </button>
            </div>
            <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              {data.recent.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No entries yet. Create your first one.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase bg-slate-50/70">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Party</th>
                      <th className="px-4 py-3">Account</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((t) => (
                      <tr
                        key={t.id}
                        onClick={() => nav(`/entry/${t.id}`)}
                        className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-slate-700 tabular">{(t.transaction_date || "").slice(0,10)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${
                            t.money_direction === "Money In" ? "bg-emerald-50 text-emerald-700" :
                            t.money_direction === "Money Out" ? "bg-red-50 text-red-700" :
                            "bg-slate-100 text-slate-700"
                          }`}>{t.entry_type}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">{t.party_name || "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{t.account || "—"}</td>
                        <td className="px-4 py-3 text-sm text-right tabular font-semibold text-slate-900">
                          {t.currency} {fmt(t.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BalancePanel({ title, icon: Icon, items, testid }) {
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Icon size={18} className="text-slate-500" />
        <h3 className="font-semibold text-slate-800">{title}</h3>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-sm text-slate-400 text-center">No balances yet</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((b, i) => (
            <li key={`${b.account}-${b.currency}-${i}`} className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-800">{b.account}</div>
                <div className="text-xs text-slate-400 font-mono">{b.currency}</div>
              </div>
              <div className={`tabular text-base font-semibold ${b.balance < 0 ? "text-red-600" : "text-slate-900"}`}>
                {b.currency} {fmt(b.balance)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
